import {useMemo, useCallback} from 'react'
import Select, {
  ActionMeta,
  SingleValue,
  GroupBase,
  MenuListProps as SelectMenuListProps,
  InputActionMeta,
} from 'react-select'
import {FixedSizeList, ListChildComponentProps} from 'react-window'
import clsx from 'clsx'
import debounce from 'lodash/debounce'
import {FormikValues} from 'formik'

// Define the option interface
interface ISelectOption {
  id: number
  value: string
  label: string
}

// Props type for ReactSelectBox
interface Props {
  formik?: FormikValues
  options?: ISelectOption[]
  fieldValue?: string
  placeholder: string
  setSelect?: (id: number, fieldValue?: string) => void
}

// Dummy options for fallback
const dummyOptions: ISelectOption[] = [
  {id: 0, value: 'optionone', label: 'optionone'},
  {id: 1, value: 'optiontwo', label: 'optiontwo'},
  {id: 2, value: 'optionthree', label: 'optionthree'},
]

// Virtualized MenuList component
const MenuList: React.FC<SelectMenuListProps<ISelectOption, false, GroupBase<ISelectOption>>> = ({
  options,
  children,
  maxHeight,
  getValue,
}) => {
  const [value] = getValue()
  const height = 35 // Height of each option
  const initialOffset = options.indexOf(value) * height

  // Ensure children is an array
  const childrenArray = Array.isArray(children) ? children : [children]

  return (
    <FixedSizeList
      height={maxHeight}
      width='100%' // Add width prop to satisfy FixedSizeList
      itemCount={childrenArray.length}
      itemSize={height}
      initialScrollOffset={initialOffset}
    >
      {({index, style}: ListChildComponentProps) => <div style={style}>{childrenArray[index]}</div>}
    </FixedSizeList>
  )
}

const OptimizedSelectBox: React.FC<Props> = ({
  formik,
  options = dummyOptions,
  placeholder,
  fieldValue = '',
  setSelect,
}) => {
  // Memoize selected option to avoid recalculating on every render
  const selectedOption = useMemo(
    () =>
      options.find(
        (option) => option.value === (formik ? formik.values[fieldValue] : fieldValue)
      ) || null,
    [options, formik, fieldValue]
  )

  // Memoize onChange handler
  const handleChange = useCallback(
    (selectedOption: SingleValue<ISelectOption>, actionMeta: ActionMeta<ISelectOption>) => {
      if (formik && fieldValue) {
        formik.setFieldValue(fieldValue, selectedOption?.value || '')
      }
      if (setSelect && selectedOption) {
        setSelect(selectedOption.id, fieldValue)
      }
    },
    [formik, fieldValue, setSelect]
  )

  // Debounce input change for search
  // eslint-disable-next-line react-hooks/exhaustive-deps
  //   const handleInputChange = useCallback(
  //     debounce((inputValue: string, {action}: ActionMeta<ISelectOption>) => {
  //       if (action === 'input-change') {
  //         // Optional: Add custom filtering logic if needed
  //       }
  //     }, 300),
  //     []
  //   )

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleInputChange = useCallback(
    debounce((inputValue: string, meta: InputActionMeta) => {
      if (meta.action === 'input-change') {
        // Optional: Add custom filtering logic if needed
      }
    }, 300),
    []
  )

  return (
    <Select
      name={fieldValue}
      className={clsx(
        'react-select-styled react-select custom-input',
        {
          'is-invalid': formik?.touched[fieldValue] && formik?.errors[fieldValue],
        },
        {
          'is-valid': formik?.touched[fieldValue] && !formik?.errors[fieldValue],
        }
      )}
      classNamePrefix='react-select'
      placeholder={placeholder}
      value={selectedOption}
      onChange={handleChange}
      onInputChange={handleInputChange}
      options={options}
      isSearchable={true}
      components={{MenuList}} // Use virtualized MenuList
    />
  )
}

export default OptimizedSelectBox
